import { removeClass, addClass, hide, show, includes, asyncForEach, getQueryString, getCurrentLanguageId } from "../../common";
import HttpClient from "../../http-client";
import { FilterParamItem } from "../filter-param-items";

export default class CheckboxOption {
    private defaultChildNumberDisplayed = 10;
    private input: HTMLInputElement;
    private sublevelContainer: HTMLElement;
    private sublevelLoaded: boolean = false;
    private paramId: number;
    private onSelectionChanged: () => void;
    private showMoreText: string; 
    private showLessText: string
    private pageId: number;
    private isHidden: boolean;
    private showMoreLinkElement: HTMLElement;

    private _listId: string;
    get listId(): string {
        return this._listId;
    }

    private _sortValue: number;
    get sortValue(): number {
        return this._sortValue;
    }

    protected _children: CheckboxOption[] = [];
    get children(): CheckboxOption[] {
        return this._children.slice();
    }

    private _name: string;
    get name(): string{
        return this._name;
    }
    
    private _baseElement: HTMLElement;
    get baseElement(): HTMLElement{
        return this._baseElement;
    }

    get isSelected(): boolean {
        return this.input.checked;
    }

    get isTopLevel(): boolean {
        return true;
    }

    public static createOptionElement(optionItem: FilterParamItem): HTMLElement{
        const div = document.createElement('div');
        div.classList.add('checkbox-value');
        div.setAttribute('data-wrapper-list-id', optionItem.ListId.toString());
        div.setAttribute('data-sort', optionItem.Sort.toString());
        
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.classList.add('checkbox-filter-input');
        input.classList.add('top-input');
        input.setAttribute('data-component-part', 'top-level-item');
        input.setAttribute('data-list-id', optionItem.ListId.toString());
        input.setAttribute('name', optionItem.Name);
        label.innerText = optionItem.Name;
        label.appendChild(input);
        div.appendChild(label);

        return div;
    }


    constructor(paramId: number, pageId: number, baseElement: HTMLElement, onSelectionChanged: () => void, showMoreText: string, showLessText: string){
        this.paramId = paramId;
        this.pageId = pageId;
        this._baseElement = baseElement;
        this.onSelectionChanged = onSelectionChanged;
        this.showMoreText = showMoreText;
        this.showLessText = showLessText;
        this._listId = this._baseElement.dataset.wrapperListId;
        this._sortValue = Number(this._baseElement.dataset.sort);
        this.input = this._baseElement.querySelector(`[data-list-id="${this._listId}"]`) as HTMLInputElement;
        this._name = this.input.name;
        this.input.addEventListener('change', () => this.selectionChanged());
    }

    public select(): void {
        this.input.checked = true;
    }

    public unselect(): void {
        this.input.checked = false;
    }
    public async loadSublevel(): Promise<void> {
        if(this.sublevelLoaded){
            return;
        }

        const children = await this.loadChildren();
        if(children && children.length > 0)
        {
            this._children = this.createOptions(children);
            this.renderChildren();
        }
        this.sublevelLoaded = true;
    }

    public selectChildren(selectedIds: string[]): void{
        this.children
            .filter(ch => includes(selectedIds, ch.listId))
            .forEach(ch => {
                ch.select();
                ch.loadSublevel();
                ch.selectChildren(selectedIds);
            });
    }

    public async selectByIds(ids: string[]): Promise<void> {
        if(!includes(ids, this.listId)) {
            return;
        }
        
        this.select();
        if(!this.isTopLevel){
            return;
        }

        await this.loadSublevel();
        await asyncForEach(this.children, (ch) => ch.selectByIds(ids))
    }

    public clear(): void {
        this.unselect();
        if(this.sublevelLoaded){
            this.children.forEach(o => o.clear());
            if(this.sublevelContainer){
                hide(this.sublevelContainer);
            }
        }
    }

    public unselectValue(listId: string): boolean {
        if(this.listId === listId && this.isSelected){
            this.unselect();
            return true;
        }
        if(this.sublevelLoaded) {
            for (let index = 0; index < this.children.length; index++) {
                const child = this.children[index];
                if(child.unselectValue(listId)){
                    return true;
                }
            }
        }
        return false;
    }

    public getSelectedValues(): CheckboxOption[] {
        let result: CheckboxOption[] = this.isSelected ? [ this ] : [];
        if(this.sublevelLoaded){
            this.children.forEach(o => result = result.concat(o.getSelectedValues()));
        }
        return result;
    }

    public hasSelectedChildren(): boolean {
        return this.children.some(ch => ch.isSelected) || this.children.some(ch => ch.hasSelectedChildren());
    }


    public updateItems(items: FilterParamItem[]){
        const item = items.find(i => i.ListId.toString() === this.listId);
        if(!item){
            this.hide();
            return;
        }
        
        this.show();
        if(this.sublevelLoaded){
            this.addChildren(item.FilterValues.filter(f => !this.children.find(ch => ch.listId === f.ListId.toString())))
        }

        if(this.isSelected){
            this.children?.forEach(ch => ch.updateItems(item.FilterValues));
        } else {
            this.children?.forEach(ch => ch.hide());
        }
        
        this.setShowMore(this.isShowMoreExpanded());
    }

    private addChildren(items: FilterParamItem[]) {
        if(items.length === 0)
            return;

        const options = this.createOptions(items);
        this._children = this._children.concat(options).sort((a,b) => a.sortValue - b.sortValue);
        if(this.sublevelContainer){
            this.baseElement.removeChild(this.sublevelContainer);
        }
        this.renderChildren();
    }

    public hide(): void{
        hide(this.baseElement);
        this.isHidden = true;
    }
    
    public show(): void{
        show(this.baseElement);
        this.isHidden = false;
    }

    protected async selectionChanged(): Promise<void> {
        if(this.isSelected){
            await this.onSelected();
        } else {
            this.onUnselected();
        }
        this.onSelectionChanged();
    }

    protected async onSelected(): Promise<void> {
        if(!this.sublevelLoaded){
            await this.loadSublevel();
        } else if (this.sublevelContainer) {
            show(this.sublevelContainer);
        }
        this.children.forEach(ch => {
            ch.select();
        });
    }

    protected onUnselected() {
        this.children.forEach(ch => {
            ch.unselect();
        });
        
        if (this.sublevelContainer) {
            hide(this.sublevelContainer);
        }
    }

    private createOptions(children: FilterParamItem[]): CheckboxOption[] {
        const options = children
            .map(child => this.createChildElement(child))
            .map(element => new CheckboxOptionChild(this.paramId, this.pageId, element, this.onSelectionChanged, this.showMoreText, this.showLessText, this));
        
        return options;
    }

    private async loadChildren(): Promise<FilterParamItem[]> {
        const urlEndpoint = `/filters/getrecursivelistitemchildren/${this.paramId}/${this.listId}`;
        const queryString = getQueryString();
        const url = queryString ? `${urlEndpoint}?${queryString}` : urlEndpoint;

        const response = await new HttpClient().fetch(url, {
            headers: {
                'PageId': this.pageId.toString(),
                'Current-Language-Id': getCurrentLanguageId()
            }
        });
        const json = await response.json();
        const children = JSON.parse(json) as FilterParamItem[];
        return children;
    }

    private renderChildren(): void{
        this.sublevelContainer = document.createElement('div');
        this.sublevelContainer.classList.add('level-container');

        let i = 1;

        this.children.forEach((item) => {
            const itemElement = item.baseElement;
            if (i > this.defaultChildNumberDisplayed || !this.isSelected) {
                hide(itemElement);
            }
            this.sublevelContainer.appendChild(itemElement);
            i++;
        });

        if (this.children.length > this.defaultChildNumberDisplayed) {
            this.showMoreLinkElement = this.createShowMoreLink();
            this.sublevelContainer.appendChild(this.showMoreLinkElement);
        }

        this.baseElement.appendChild(this.sublevelContainer);
    }
    
    private createChildElement(filterItem: FilterParamItem): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.classList.add('checkbox-value-child');
        wrapper.dataset.wrapperListId = filterItem.ListId.toString();

        const label = document.createElement('label');
        label.append('&emsp;');

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.classList.add('checkbox-filter-input');
        input.dataset.listId = filterItem.ListId.toString();
        input.dataset.parentId = this.listId.toString();
        input.name = filterItem.Name;

        const span = document.createElement('span');
        span.dataset.linkListId = filterItem.ListId.toString();
        span.classList.add('checkbox-fake-link');
        span.innerText = filterItem.Name;

        label.appendChild(input);
        label.appendChild(span);
        wrapper.appendChild(label);
        
        return wrapper;
    }

    private createShowMoreLink(): HTMLElement {
        const element = document.createElement('div');
        element.dataset.toggleState = 'collapsed';
        element.dataset.dataListId = this.listId;
        element.classList.add('show-more-link');
        element.innerHTML = this.showMoreText;
        element.addEventListener('click', () => this.toggleShowMore());
        return element;
    }
    

    private toggleShowMore() {
        this.setShowMore(!this.isShowMoreExpanded());
    }

    private isShowMoreExpanded(): boolean{
        return this.showMoreLinkElement && this.showMoreLinkElement.dataset.toggleState === 'expanded';
    }

    private setShowMore(expanded: boolean){
        if(!this.showMoreLinkElement)
            return;

        if(expanded){
            this.showAllSubvalues();
            this.showMoreLinkElement.dataset.toggleState = 'expanded';
            this.showMoreLinkElement.classList.remove('show-more-link');
            this.showMoreLinkElement.classList.add('show-less-link');
            this.showMoreLinkElement.innerHTML = this.showLessText;
        } else {
            this.hideSomeSubValues();
            this.showMoreLinkElement.dataset.toggleState = 'collapsed';
            this.showMoreLinkElement.classList.remove('show-less-link');
            this.showMoreLinkElement.classList.add('show-more-link');
            this.showMoreLinkElement.innerHTML = this.showMoreText;
        }
    }

    private showAllSubvalues(): any {
        this.children.forEach(ch => show(ch.baseElement));
    }

    private hideSomeSubValues(): any {
        if (this.children.length <= this.defaultChildNumberDisplayed) {
            return;
        }
        const childrenToHide = this.children.slice(this.defaultChildNumberDisplayed);
        childrenToHide.forEach(ch => hide(ch.baseElement));
    }
}

class CheckboxOptionChild extends CheckboxOption {
    private parent: CheckboxOption;
    private linkElement: HTMLElement;
    private selectedLinkClass = 'selected-fake-link';

    get isTopLevel(): boolean {
        return false;
    }

    constructor(paramId: number, pageId: number, baseElement: HTMLElement, onSelectionChanged: () => void, showMoreText: string, showLessText: string, parent: CheckboxOption){
        super(paramId, pageId, baseElement, onSelectionChanged, showMoreText, showLessText);
        this.parent = parent;
        this.linkElement = this.baseElement.querySelector('[data-link-list-id]');
        this.linkElement.addEventListener('click', t => { t.preventDefault(); this.selectOnly() });
    }

    private selectOnly() {
        this.parent.children.forEach(ch => ch.unselect());
        this.select();
        this.selectionChanged();
    }

    protected async onSelected(): Promise<void> {
        super.onSelected();
        addClass(this.linkElement, this.selectedLinkClass);
    }

    protected async onUnselected(): Promise<void> {
        super.onUnselected();
        removeClass(this.linkElement, this.selectedLinkClass);
    }
}
